/**
 * What each side of a trade actually puts on the table
 * (q-p2triage-fe-player-trust-and-ux; Principle 6). The trade window used to
 * summarise both offers as "N items, ◈ S" — so at the confirm step a partner who
 * swapped a rare blade for a common one, or added resource legs, read exactly
 * the same. This lists every leg: each item, each resource stack, the Shards.
 *
 * Items resolve to names from `TradeStatePayload.items` — server-filled display
 * fields for ids the offering account owns (`q-trade-state-item-display`) — and,
 * for your own items, from your inventory snapshot. An id neither names (a bogus
 * id, a failed lookup) shows as a short id: distinct ids read distinct, and a swap
 * still changes the line. The server re-validates everything at commit; this only
 * shows. Pure; node-testable.
 */

import type { TradeOfferWire } from '@great-cloud/shared/client';

import { nameOf } from '../playerText.js';

/** The `inventorySnapshot.items` fields this reads. */
export interface TradeItemView {
  id: string;
  name: string;
  rarity?: string;
  quality?: string;
}

export interface TradeOfferLine {
  kind: 'item' | 'resource' | 'shards';
  text: string;
  /** For item lines: the rarity class to colour by, when known. */
  rarity?: string;
}

/**
 * A short, stable label for an item id this client cannot name. The TAIL, not the
 * head: time-ordered UUIDs minted together share their leading digits.
 */
export function shortItemId(id: string): string {
  return id.length > 8 ? `item …${id.slice(-8)}` : `item ${id}`;
}

/** Every leg of one side's offer, in wire order; an empty offer is one "nothing" line. */
export function tradeOfferLines(
  offer: TradeOfferWire,
  known: readonly TradeItemView[] = [],
  displays: readonly TradeItemView[] = [],
): TradeOfferLine[] {
  // The server's display fields win over the local snapshot (fresher mid-trade).
  const byId = new Map([...known, ...displays].map((it) => [it.id, it]));
  const lines: TradeOfferLine[] = offer.itemIds.map((id) => {
    const it = byId.get(id);
    if (!it) return { kind: 'item', text: shortItemId(id) };
    const detail = [it.quality, it.rarity].filter(Boolean).join(' · ');
    return {
      kind: 'item',
      text: detail ? `${it.name} (${detail})` : it.name,
      ...(it.rarity ? { rarity: it.rarity } : {}),
    };
  });
  for (const r of offer.resources)
    lines.push({
      kind: 'resource',
      text: `${nameOf('resources', r.resourceId)} q${r.quality} ×${r.quantity}`,
    });
  if (offer.shards > 0) lines.push({ kind: 'shards', text: `◈ ${offer.shards}` });
  if (lines.length === 0) lines.push({ kind: 'shards', text: 'nothing' });
  return lines;
}
